import { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { uploadImage } from "../services/uploads-api";

/** FR-072: a basic palette a CO Member can reach for without a colour picker. The first four are
 * the brand tokens from plan.md (ink / brand blue / accent / body); the rest are the plain
 * red-orange-yellow-green-purple set the product owner asked for, since explanations often need
 * "this bit is wrong" / "this bit is right" emphasis that the brand palette alone can't express. */
const COLORS: { hex: string; label: string }[] = [
  { hex: "#094067", label: "Dark blue" },
  { hex: "#1A62FF", label: "Brand blue" },
  { hex: "#5F6C7B", label: "Grey" },
  { hex: "#000000", label: "Black" },
  { hex: "#EF4565", label: "Red" },
  { hex: "#F97316", label: "Orange" },
  { hex: "#EAB308", label: "Yellow" },
  { hex: "#16A34A", label: "Green" },
  { hex: "#9333EA", label: "Purple" },
];

/**
 * FR-028: rich-formatted answer explanation editor — bold, italic, text color, embedded
 * images, tables. T041: image insertion goes through POST /co/uploads/image (uploads-api.ts).
 */
export function AnswerExplanationEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  async function handleImagePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow picking the same file again later
    if (!file || !editor) return;
    try {
      const { url } = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch {
      // The 5MB/type validation already happened client-side isn't required — the backend
      // enforces both (research.md §7) — a failed upload just leaves the editor unchanged.
    }
  }

  return (
    <div className="rounded border border-surface-alt">
      <div className="flex flex-wrap gap-1 border-b border-surface-alt bg-surface-alt p-2">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`rounded px-2 py-1 text-sm font-bold ${editor.isActive("bold") ? "bg-brand text-surface" : "bg-surface"}`}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`rounded px-2 py-1 text-sm italic ${editor.isActive("italic") ? "bg-brand text-surface" : "bg-surface"}`}
        >
          I
        </button>
        {COLORS.map(({ hex, label }) => (
          <button
            key={hex}
            type="button"
            aria-label={`Text color ${label}`}
            title={label}
            onClick={() => editor.chain().focus().setColor(hex).run()}
            className="h-7 w-7 rounded border border-surface-alt"
            style={{ backgroundColor: hex }}
          />
        ))}
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetColor().run()}
          className="rounded bg-surface px-2 py-1 text-sm text-body"
          title="Remove text colour"
        >
          Clear colour
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="rounded bg-surface px-2 py-1 text-sm text-body"
        >
          Insert image
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImagePick}
        />
        <button
          type="button"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()
          }
          className="rounded bg-surface px-2 py-1 text-sm text-body"
        >
          Insert table
        </button>
      </div>
      {/* `rich-text` supplies the table/image styling Preflight strips — see styles/rich-text.css.
          Without it an inserted table renders with no borders at all and looks like blank space. */}
      <EditorContent editor={editor} className="rich-text prose max-w-none p-3 font-sans text-ink" />
    </div>
  );
}
